import React, {
  useCallback,
  useRef,
} from 'react';
import IconButton from 'core/components/IconButton';
import AddchartIcon from '@mui/icons-material/Addchart';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import noop from 'lodash/noop';
import {
  VisitNoteSummaryDTO,
} from 'dtos';
import {
  formatDate,
} from 'utils/date';
import PrinterIcon from 'core/icons/printer';
import {
  postApiVisitNotePrintVisitNotePdfEncounterId,
} from 'endpoints';
import {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import PastNoteDetails from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/PastNotes/PastNoteDetails';
import Spinner from 'pages/Dashboard/components/Spinner';
import clsx from 'clsx';
import Tooltip from 'core/components/Tooltip';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  isSaltContentAvailable,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/helper';
import {
  usePastNotesContext,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/PastNotes/PastNotesContext';
import {
  isNilOrZero,
} from '@populateui/utils';

type Props = {
  note: VisitNoteSummaryDTO;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

export default function PastNoteRow({
  note,
  isExpanded,
  onToggleExpand,
}: Props) {
  const { patientId, isCurrentNoteFinalized } = usePastNotesContext();
  const rowRef = useRef<HTMLDivElement>(null);
  const {
    print,
    isPrinting,
  } = usePrintMediaRequest<[number]>(postApiVisitNotePrintVisitNotePdfEncounterId);

  const {
    visitDateTime,
    reasonForVisit,
    isFinalized,
    encounterId,
    visitNoteId = 0,
  } = note;

  const hasContent = isSaltContentAvailable(note, 'master');

  const handlePrint = () => {
    if (!isNilOrZero(encounterId)) {
      print([encounterId ?? 0]);
    }
  };

  const scrollIntoView = useCallback(() => {
    setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 0);
  }, []);

  const reasonForVisitRef = useRef<HTMLSpanElement>(null);
  const applyTooltip = hasContent ? 'Apply to current visit note' : 'Visit note is empty';
  const expandTooltip = hasContent ? 'Show details' : 'Visit note is empty, cannot expand';
  const applyDisabled = !hasContent || isCurrentNoteFinalized || isNilOrZero(visitNoteId);

  return (
    <div
      ref={rowRef}
      className="flex flex-col border-b border-gray-100 last:border-b-0"
    >
      <div
        className={clsx(
          'group flex items-center gap-1 py-2 px-4 min-w-0 shrink-0 rounded-lg !border-l-4 border-l-transparent',
          {
            'hover:bg-slate-100': hasContent,
            'opacity-70': !hasContent,
            'sticky top-0 z-[100] bg-slate-100 !border-l-slate-500 shadow-sm': isExpanded,
          },
        )}
        role="button"
        tabIndex={-1}
        onKeyDown={noop}
        onClick={hasContent ? onToggleExpand : noop}
      >
        <span
          className={clsx('text-xs shrink-0 font-medium', {
            'group-hover:text-blue-500': hasContent,
            'text-blue-500': isExpanded,
            'text-gray-500': !isExpanded,
          })}
        >
          {visitDateTime ? formatDate(visitDateTime) : '—'}
        </span>
        <span
          className={clsx('text-xs shrink-0 font-normal', {
            'group-hover:text-blue-500': hasContent,
            'text-blue-700': isExpanded,
            'text-gray-700': !isExpanded,
          })}
        >
          •
        </span>
        <Tooltip title={reasonForVisit ?? '—'} auto ref={reasonForVisitRef}>
          <span
            className={clsx(
              'text-xs truncate flex-1 min-w-0 font-semibold',
              {
                'group-hover:text-blue-500': hasContent,
                'text-blue-500': isExpanded,
                'text-gray-700': !isExpanded,
              },
            )}
            ref={reasonForVisitRef}
          >
            {reasonForVisit ?? '—'}
          </span>
        </Tooltip>
        <div className="flex items-center gap-0.5 shrink-0">
          <IconButton
            tooltip={isFinalized ? 'Print finalized note' : 'Note is not finalized yet'}
            size="small"
            disabled={!isFinalized || isPrinting}
            onClick={(e) => {
              e.stopPropagation();
              handlePrint();
            }}
            aria-label="Print"
          >
            {isPrinting ? (
              <Spinner className="!w-4 !h-4" />
            ) : (
              <PrinterIcon className="!w-4 !h-4" />
            )}
          </IconButton>
          <IconButton
            tooltip={isCurrentNoteFinalized ? 'Cannot apply to finalized note' : applyTooltip}
            size="small"
            disabled={applyDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!isNilOrZero(visitNoteId)) {
                visitNoteEventChannel.emit('onApplyPastNote', visitNoteId);
              }
            }}
            aria-label="Apply"
          >
            <AddchartIcon className="!w-4 !h-4 " />
          </IconButton>
          <IconButton
            tooltip={isExpanded ? 'Hide details' : expandTooltip}
            size="small"
            disabled={!hasContent}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            aria-label={isExpanded ? 'Hide details' : 'Show details'}
          >
            {isExpanded ? (
              <KeyboardArrowUpRoundedIcon className="!w-4 !h-4" />
            ) : (
              <KeyboardArrowDownRoundedIcon className="!w-4 !h-4" />
            )}
          </IconButton>
        </div>
      </div>
      {isExpanded && !isNilOrZero(visitNoteId) && (
        <div className={clsx('bg-gray-50')}>
          <PastNoteDetails
            visitNoteId={visitNoteId}
            patientId={patientId}
            isCurrentNoteFinalized={isCurrentNoteFinalized}
            onDataLoaded={scrollIntoView}
          />
        </div>
      )}
    </div>
  );
}
